import {
  Badge,
  Box,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  IconArrowRight,
  IconChecks,
  IconClockHour4,
  IconMessageCircle,
  IconRepeat,
  IconRosetteDiscountCheckFilled,
  IconShieldCheck,
  IconStarFilled,
} from "@tabler/icons-react";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createOrganizationSchema,
} from "../../seo/utils";
import {
  formatReviewCount,
  getAllReviews,
  getRatingDistribution,
  getReviewCategories,
  getReviewMetrics,
  getReviewSummary,
} from "../../Services/review.service";
import ReviewCard from "./ReviewCard";
import ReviewsTrustBar from "./ReviewsTrustBar";
import "./Reviews.css";

const FILTER_ALL = "All";
const STAR_ALL = "All ratings";
const INITIAL_VISIBLE_REVIEW_COUNT = 6;

const metricIcons = {
  recommended: IconShieldCheck,
  "on-time": IconClockHour4,
  clarity: IconMessageCircle,
  repeat: IconRepeat,
};

function ReviewsPage() {
  const theme = useMantineTheme();
  const summary = getReviewSummary();
  const ratingDistribution = getRatingDistribution();
  const reviewMetrics = getReviewMetrics();
  const categories = [FILTER_ALL, ...getReviewCategories()];
  const [activeCategory, setActiveCategory] = useState(FILTER_ALL);
  const [activeRating, setActiveRating] = useState(STAR_ALL);
  const [visibleReviewCount, setVisibleReviewCount] = useState(
    INITIAL_VISIBLE_REVIEW_COUNT,
  );

  const allReviews = getAllReviews();
  const filteredReviews = allReviews.filter((review) => {
    const matchesCategory =
      activeCategory === FILTER_ALL || review.category === activeCategory;
    const matchesRating =
      activeRating === STAR_ALL || review.rating >= Number(activeRating);

    return matchesCategory && matchesRating;
  });
  const visibleReviews = filteredReviews.slice(0, visibleReviewCount);

  return (
    <main className="subjectbuddy-reviews-page">
      <Seo
        title="Student Reviews and Trust Signals | SubjectBuddy"
        description="Explore SubjectBuddy student reviews, satisfaction metrics, rating distribution, and verified feedback across assignment, dissertation, coding, and academic writing support."
        path="/reviews"
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Reviews", path: "/reviews" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "SubjectBuddy academic support",
            description:
              "Student reviews and feedback for SubjectBuddy academic guidance and assignment support.",
            brand: {
              "@type": "Organization",
              name: "SubjectBuddy",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: summary.averageRating.toFixed(1),
              reviewCount: String(summary.totalReviews),
            },
            url: buildCanonicalUrl("/reviews"),
          },
        ]}
      />

      <Box className="subjectbuddy-reviews-hero">
        <Container size="xl">
          <div className="subjectbuddy-reviews-hero-grid">
            <Paper radius="2rem" p="xl" className="subjectbuddy-reviews-summary-panel">
              <Stack gap="lg">
                <Badge
                  color="green"
                  radius="xl"
                  size="lg"
                  style={{ width: "fit-content" }}
                  leftSection={<IconRosetteDiscountCheckFilled size={14} />}
                >
                  Verified student feedback
                </Badge>
                <Stack gap="sm">
                  <Title order={1} className="subjectbuddy-reviews-page-title">
                    SubjectBuddy reviews
                  </Title>
                  <Text size="lg" c="dimmed" maw={720} style={{ lineHeight: 1.8 }}>
                    A dedicated snapshot of how students describe SubjectBuddy:
                    clearer explanations, better structure, calmer deadline
                    support, and more confidence with the final submission.
                  </Text>
                </Stack>

                <div className="subjectbuddy-reviews-score-line">
                  <div className="subjectbuddy-reviews-score-block">
                    <Text className="subjectbuddy-reviews-score-value">
                      {summary.averageRating.toFixed(1)}
                    </Text>
                    <ReviewSummaryStars />
                  </div>
                  <div>
                    <Text fw={800}>
                      {formatReviewCount(summary.totalReviews)} reviews · Excellent
                    </Text>
                    <Text c="dimmed">
                      {formatReviewCount(summary.verifiedCount)} marked as verified
                      student feedback.
                    </Text>
                  </div>
                </div>

                <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
                  <ReviewStatCard
                    value={`${summary.recommendationRate}%`}
                    label="would recommend"
                  />
                  <ReviewStatCard
                    value={`${summary.onTimeRate}%`}
                    label="deadline support mentions"
                  />
                  <ReviewStatCard
                    value={`${summary.clarityRate}%`}
                    label="clear explanation mentions"
                  />
                  <ReviewStatCard
                    value={`${summary.supportResponseMinutes} min`}
                    label="typical response time"
                  />
                </SimpleGrid>
              </Stack>
            </Paper>

            <Paper radius="2rem" p="xl" className="subjectbuddy-reviews-distribution-panel">
              <Stack gap="lg">
                <Group justify="space-between" align="center">
                  <Title order={2}>Rating distribution</Title>
                  <IconStarFilled size={20} color={theme.colors.yellow[6]} />
                </Group>
                <Stack gap="sm">
                  {ratingDistribution.map((row) => (
                    <div key={row.stars} className="subjectbuddy-reviews-distribution-row">
                      <Text fw={700} size="sm" className="subjectbuddy-reviews-distribution-label">
                        {row.label}
                      </Text>
                      <div className="subjectbuddy-reviews-distribution-track">
                        <div
                          className="subjectbuddy-reviews-distribution-fill"
                          style={{ width: `${row.percentage}%` }}
                        />
                      </div>
                      <Text fw={700} size="sm">
                        {row.percentage.toFixed(1)}%
                      </Text>
                    </div>
                  ))}
                </Stack>
                <Text size="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                  Most student feedback highlights clarity, on-time support, and
                  confidence-building guidance rather than only last-minute fixes.
                </Text>
              </Stack>
            </Paper>
          </div>
        </Container>
      </Box>

      <Container size="xl" className="subjectbuddy-reviews-main">
        <Stack gap={70}>
          <ReviewsTrustBar showLink={false} />

          <section aria-labelledby="reviews-metrics-heading">
            <Stack gap="lg">
              <div>
                <Title order={2} id="reviews-metrics-heading">
                  Trust signals students mention most
                </Title>
                <Text c="dimmed" maw={860} style={{ lineHeight: 1.8 }}>
                  Inspired by the review-summary structure in the reference pages,
                  these metrics make the feedback easier to scan before you dig
                  into individual reviews.
                </Text>
              </div>

              <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="lg">
                {reviewMetrics.map((metric) => {
                  const Icon =
                    metricIcons[metric.key as keyof typeof metricIcons] || IconChecks;

                  return (
                    <Paper
                      key={metric.key}
                      withBorder
                      radius="xl"
                      p="lg"
                      className="subjectbuddy-review-metric-card"
                    >
                      <Stack gap="sm">
                        <div className="subjectbuddy-review-metric-icon">
                          <Icon size={20} />
                        </div>
                        <Text fw={800} size="xl" c={theme.colors.green[8]}>
                          {metric.value}
                        </Text>
                        <Text fw={700}>{metric.label}</Text>
                        <Text size="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
                          {metric.detail}
                        </Text>
                      </Stack>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            </Stack>
          </section>

          <section aria-labelledby="reviews-feed-heading">
            <Stack gap="lg">
              <div>
                <Title order={2} id="reviews-feed-heading">
                  Student review feed
                </Title>
                <Text c="dimmed" maw={860} style={{ lineHeight: 1.8 }}>
                  Browse the latest feedback by category or by rating band.
                </Text>
              </div>

              <div className="subjectbuddy-review-filters">
                <Stack gap="sm">
                  <Text fw={700} size="sm">
                    Filter by category
                  </Text>
                  <Group gap="sm">
                    {categories.map((category) => (
                      <Button
                        key={category}
                        type="button"
                        variant={activeCategory === category ? "filled" : "light"}
                        color={activeCategory === category ? "green" : "gray"}
                        radius="xl"
                        onClick={() => {
                          setActiveCategory(category);
                          setVisibleReviewCount(INITIAL_VISIBLE_REVIEW_COUNT);
                        }}
                      >
                        {category}
                      </Button>
                    ))}
                  </Group>
                </Stack>

                <Stack gap="sm">
                  <Text fw={700} size="sm">
                    Filter by rating
                  </Text>
                  <Group gap="sm">
                    {[STAR_ALL, "5", "4"].map((rating) => (
                      <Button
                        key={rating}
                        type="button"
                        variant={activeRating === rating ? "filled" : "light"}
                        color={activeRating === rating ? "green" : "gray"}
                        radius="xl"
                        onClick={() => {
                          setActiveRating(rating);
                          setVisibleReviewCount(INITIAL_VISIBLE_REVIEW_COUNT);
                        }}
                      >
                        {rating === STAR_ALL ? rating : `${rating}+ stars`}
                      </Button>
                    ))}
                  </Group>
                </Stack>
              </div>

              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
                {visibleReviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </SimpleGrid>

              <Group justify="space-between" align="center" wrap="wrap">
                <Text c="dimmed">
                  Showing {visibleReviews.length} of {filteredReviews.length} matching reviews.
                </Text>
                {visibleReviews.length < filteredReviews.length ? (
                  <Button
                    type="button"
                    variant="light"
                    color="green"
                    radius="xl"
                    onClick={() =>
                      setVisibleReviewCount((currentCount) => currentCount + 4)
                    }
                  >
                    Load more reviews
                  </Button>
                ) : null}
              </Group>
            </Stack>
          </section>

          <Paper radius="2rem" p="xl" className="subjectbuddy-reviews-final-cta">
            <Group justify="space-between" align="center" gap="xl" wrap="wrap">
              <Stack gap="sm" maw={780}>
                <Text tt="uppercase" fw={800} size="sm" c={theme.colors.green[7]}>
                  Need support now?
                </Text>
                <Title order={2}>See why students return to SubjectBuddy</Title>
                <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                  If you are comparing options, start with the pages students
                  visit most often for dissertation, coding, statistics, and
                  assignment-writing support.
                </Text>
              </Stack>
              <Group gap="sm">
                <Button
                  component={Link}
                  to="/assignment-help"
                  color="green"
                  radius="xl"
                >
                  Explore assignment help
                </Button>
                <Button
                  component={Link}
                  to="/blogs"
                  variant="light"
                  color="green"
                  radius="xl"
                  rightSection={<IconArrowRight size={16} />}
                >
                  Read student guides
                </Button>
              </Group>
            </Group>
          </Paper>
        </Stack>
      </Container>
    </main>
  );
}

function ReviewSummaryStars() {
  return (
    <Group gap={4} wrap="nowrap">
      {Array.from({ length: 5 }).map((_, index) => (
        <IconStarFilled
          key={`summary-star-${index}`}
          size={18}
          color="#facc15"
        />
      ))}
    </Group>
  );
}

interface ReviewStatCardProps {
  value: string;
  label: string;
}

function ReviewStatCard({ value, label }: ReviewStatCardProps) {
  return (
    <Paper
      withBorder
      radius="xl"
      p="md"
      className="subjectbuddy-reviews-mini-stat"
    >
      <Stack gap={2}>
        <Text fw={800} size="lg">
          {value}
        </Text>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Stack>
    </Paper>
  );
}

export default ReviewsPage;
